import { useEffect, useState, useCallback, useMemo, useRef, } from 'react';
import { App, Form } from 'antd';
import { fetchInputHistory } from 'api/user-api';
import { getCurrentUiLocale } from 'libs/locales/ui';
import { getProjectLanguage, fetchTrsxLabels } from 'api/project-api';
import { useSession } from 'components/Authenticated';
import { txt } from 'libs/i18n';
import { aiAssignmentsSignal, loadAiAssignments } from 'screens/DashboardScreen/pages/EditorPage/signalsStore/aiAssignments';
import { getAssignmentOptions, getInitialAiAssignment } from './assignment-options';
const CUSTOM_PROMPT_HISTORY_COUNT = 1;
export const useAiFormInit = (projectConfig, defaultTrsxLabel) => {
    const [isInitializing, setIsInitializing] = useState(true);
    const [aiAssignments, setAiAssignments] = useState([]);
    const [trsxLabels, setTrsxLabels] = useState([]);
    const projectLanguageRef = useRef(null);
    const { session } = useSession();
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const projectId = useMemo(() => {
        var _a;
        const { projectType } = projectConfig;
        return projectType === 'single' ? projectConfig.projectId : (_a = projectConfig.projectList[0]) === null || _a === void 0 ? void 0 : _a.id;
    }, [projectConfig]);
    const initAiAssignments = useCallback(async () => {
        if (aiAssignmentsSignal.value.status !== 'loaded') {
            await loadAiAssignments(session.connection);
        }
        const state = aiAssignmentsSignal.value;
        if (state.status !== 'loaded') {
            throw new Error('Failed to fetch AI assignments.');
        }
        const options = getAssignmentOptions(state.assignments);
        setAiAssignments(options);
        form.setFieldValue('promptId', getInitialAiAssignment(options, session.teamSettings.aiPromptId));
    }, [session.connection, form, getInitialAiAssignment]);
    const loadCustomPromptHistory = useCallback(async () => {
        const history = await fetchInputHistory(session.connection, 'customPrompt', 0, CUSTOM_PROMPT_HISTORY_COUNT);
        form.setFieldValue('customPrompt', history[0]);
    }, [session.connection, form]);
    const loadProjectLanguage = useCallback(async () => {
        const language = await getProjectLanguage(session.connection, projectId);
        projectLanguageRef.current = language;
        form.setFieldValue('language', language !== null && language !== void 0 ? language : getCurrentUiLocale());
    }, [session.connection, projectId, form]);
    const loadTrsxLabels = useCallback(async () => {
        if (projectConfig.projectType !== 'single') {
            return;
        }
        try {
            const { labels } = await fetchTrsxLabels(session.connection, projectConfig.projectId);
            setTrsxLabels(labels);
            if (labels.length > 1) {
                form.setFieldValue('trsxLabel', defaultTrsxLabel !== null && defaultTrsxLabel !== void 0 ? defaultTrsxLabel : labels[0]);
            }
        }
        catch (error) {
            global.console.warn('Failed to load TRSX labels:', error);
        }
    }, [session.connection, projectConfig, defaultTrsxLabel, form]);
    useEffect(() => {
        const initializeComponent = async () => {
            setIsInitializing(true);
            try {
                await Promise.all([
                    initAiAssignments(),
                    loadCustomPromptHistory(),
                    loadProjectLanguage(),
                    loadTrsxLabels(),
                ]);
            }
            catch (error) {
                global.console.error('Initialization failed:', error);
                void message.error(txt('failedToLoadAiTools'));
            }
            finally {
                setIsInitializing(false);
            }
        };
        void initializeComponent();
    }, []);
    return ({
        form,
        aiAssignments,
        loading: isInitializing,
        projectLanguage: projectLanguageRef.current,
        trsxLabels,
    });
};
